import { BorderRadius, Color, pxToNumber, rwd, Spacing } from '@billy/design-system'
import styled from '@emotion/styled'

import { Container as AbsoluteContainer } from '../Container'
import { StackLayout, StackLayoutBox, StackLayoutBoxProps } from '../StackLayout'

const HORIZONTAL_PADDING = 30
const SECTIONS_DISTANCE = 30

const SMALL_GAP = pxToNumber(Spacing.S8)
const BIG_GAP = 100

const SMALL_GAP_OFFSET = BIG_GAP / 4 - SMALL_GAP / 2
const BIG_GAP_OFFSET = BIG_GAP / 2 - SMALL_GAP / 2

export const MainContainer = styled.main`
  display: flex;
  flex: 1 0 auto;
  position: relative;
`

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  background-color: ${Color.Grey100};
`

export const FormFileStackLayout = styled(StackLayout)`
  flex: 1 0 auto;
  height: 100%;
  padding-right: ${HORIZONTAL_PADDING}px;
  padding-left: ${HORIZONTAL_PADDING}px;

  ${rwd.equalOrLessThan('large')} {
    padding-bottom: 0;
  }
`

type FormFilesLayoutBoxProps = {
  fourInRow?: boolean
}

export const FormFilesLayoutBox = styled(StackLayoutBox)<FormFilesLayoutBoxProps>`
  display: grid;
  gap: ${Spacing.S12};
  grid-template-columns: ${({ fourInRow }) => (fourInRow ? '1fr 1fr 1fr 1fr' : '1fr 1fr')};
  grid-template-rows: min-content;
  width: calc(100% - 5px);

  ${rwd.equalOrLessThan('large')} {
    column-gap: ${({ fourInRow }) => (fourInRow ? `${SMALL_GAP}px` : `${BIG_GAP}px`)};
    margin-right: 0;

    ${({ fourInRow }) => {
      if (fourInRow) {
        return `
          > * {
            &:first-of-type {
              margin-right: ${SMALL_GAP_OFFSET}px;
            }

            &:nth-of-type(2) {
              margin-left: -${SMALL_GAP_OFFSET}px;
              margin-right: ${BIG_GAP_OFFSET}px;
            }

            &:nth-of-type(3) {
              margin-left: ${BIG_GAP_OFFSET}px;
              margin-right: -${SMALL_GAP_OFFSET}px;
            }

            &:last-of-type {
              margin-left: ${SMALL_GAP_OFFSET}px;
            }
          }
        `
      }
    }}
  }
`

export const FormContainer = styled(StackLayout)`
  position: relative;

  ${rwd.equalOrLessThan('large')} {
    height: 60%;
  }
`

type FilesContainerProps = StackLayoutBoxProps

export const FilesContainer = styled(StackLayoutBox)<FilesContainerProps>`
  width: 100%;
  height: 100%;
  padding-bottom: 35px;
  position: relative;

  ${rwd.equalOrLessThan('large')} {
    width: calc(100% + 60px);
    height: 40%;
    margin-top: 20px;
    margin-right: -${HORIZONTAL_PADDING}px;
    margin-left: -${HORIZONTAL_PADDING}px;
    padding: 0 ${HORIZONTAL_PADDING}px;
    background-color: ${Color.Grey100};
  }
`

export const FormAbsoluteContainer = styled(AbsoluteContainer)`
  width: calc(100% - ${SECTIONS_DISTANCE}px);
  padding-right: ${Spacing.S8};
  right: ${SECTIONS_DISTANCE}px;

  ${rwd.equalOrLessThan('large')} {
    width: calc(100% + ${HORIZONTAL_PADDING}px);
    margin-right: ${HORIZONTAL_PADDING}px;
    padding-top: 10px;
    padding-right: ${HORIZONTAL_PADDING}px;
  }
`

export const FilesAbsoluteContainer = styled(AbsoluteContainer)`
  max-height: 100%;

  ${rwd.greaterThan('large')} {
    padding-bottom: 35px;
    overflow: hidden;
  }

  ${rwd.equalOrLessThan('large')} {
    padding-top: 10px;
  }
`

interface FileWrapperProps {
  fullHeight?: boolean
  isError?: boolean
}

export const FileWrapper = styled.div<FileWrapperProps>`
  padding: ${Spacing.S8};
  border: 1px solid ${Color.Grey200};
  border-radius: ${BorderRadius.R12};
  background-color: ${Color.White};

  ${({ isError }) =>
    isError &&
    `
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
  `}

  ${rwd.greaterThan('large')} {
    height: 100%;
  }

  ${rwd.equalOrLessThan('large')} {
    align-self: center;
    width: 640px;
    border-color: ${Color.Grey200};
    aspect-ratio: 1.41;
  }
`

export const FileMock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${Color.Grey100};
`
